import type { CartPanelItem } from '../../hooks/useCartPanelItems'
import { isCartPanelItemCheckoutReady } from '../../utils/cartPanelUtils'
import { isSavedFlightSearchExpired } from '../../utils/flightCartPanelUtils'

export const CHAT_CART_CATEGORY = {
  ALL: 'all',
  FLIGHT: 'flight',
  ACCOMMODATION: 'accommodation',
  EXPERIENCE: 'experience',
} as const

export type ChatCartCategory = typeof CHAT_CART_CATEGORY[keyof typeof CHAT_CART_CATEGORY]

type ChatCartProductType = Exclude<ChatCartCategory, typeof CHAT_CART_CATEGORY.ALL>

const CHAT_CART_PRODUCT_CATEGORIES: ReadonlyArray<ChatCartProductType> = [
  CHAT_CART_CATEGORY.FLIGHT,
  CHAT_CART_CATEGORY.ACCOMMODATION,
  CHAT_CART_CATEGORY.EXPERIENCE,
]

export interface ChatCartCategoryOption {
  category: ChatCartCategory
  count: number
}

/**
 * Filter chips are only worth showing when there is more than one product type to
 * filter between, matching the LuxKit guidance that a filter chip row should never
 * be rendered for a single option.
 */
export function getChatCartCategoryOptions(
  items: Array<CartPanelItem>,
): Array<ChatCartCategoryOption> {
  const productOptions = CHAT_CART_PRODUCT_CATEGORIES
    .map(category => ({
      category,
      count: items.filter(item => item.productType === category).length,
    }))
    .filter(option => option.count > 0)

  if (productOptions.length < 2) return []

  return [{ category: CHAT_CART_CATEGORY.ALL, count: items.length }, ...productOptions]
}

export function filterChatCartItems(
  items: Array<CartPanelItem>,
  category: ChatCartCategory,
): Array<CartPanelItem> {
  if (category === CHAT_CART_CATEGORY.ALL) return items
  return items.filter(item => item.productType === category)
}

export function countChatCartBookableItems(items: Array<CartPanelItem>): number {
  return items.filter(isCartPanelItemCheckoutReady).length
}

export function countChatCartBlockedItems(items: Array<CartPanelItem>): number {
  return items.filter(item => !item.cartExcluded && !isCartPanelItemCheckoutReady(item)).length
}

export const CHAT_CART_ROW_ACTION = {
  ADD_DATES: 'add_dates',
  CHOOSE_DATES: 'choose_dates',
  CHOOSE_PACKAGE: 'choose_package',
  CHOOSE_TICKETS: 'choose_tickets',
  CHOOSE_DIFFERENT_TICKETS: 'choose_different_tickets',
  CHOOSE_DATE: 'choose_date',
  CHOOSE_TIME: 'choose_time',
  CHOOSE_FLIGHT: 'choose_flight',
  REFRESH_FLIGHT: 'refresh_flight',
  CHANGE_FLIGHT: 'change_flight',
} as const

export type ChatCartRowAction = typeof CHAT_CART_ROW_ACTION[keyof typeof CHAT_CART_ROW_ACTION]

/**
 * The single action offered on a compact cart row for an item that cannot be booked
 * yet. Mirrors the full cart panel tiles: accommodation blockers are all resolved in
 * the offer drawer, while experience and flight blockers each have their own picker.
 * Availability problems the customer cannot act on (sold out, rate withdrawn) return
 * undefined so the row shows a status only.
 */
export function resolveChatCartRowAction(item: CartPanelItem): ChatCartRowAction | undefined {
  if (item.isBooked || item.cartExcluded) return undefined

  switch (item.productType) {
    case 'accommodation': {
      // Mirrors CartPanelActiveHotelTile / CartPanelDurationUnavailableTile. The offer
      // drawer is where missing dates and missing packages get fixed, but a sold-out
      // property has nothing to pick there, so it reports status rather than offering a
      // dead-end link (the tile suppresses its banner the same way, via `!isSoldOut`).
      //
      // Order matters: includeInTotal is false for *every* non-active status, so it can
      // only be read once the unbookable cases have been ruled out. Reading it first
      // labels a sold-out hotel "Add dates".
      if (!item.offerId) return undefined
      if (item.cartStatus === 'property_sold_out' || item.pricing.isAvailable === false) return undefined
      if (item.cartStatus !== 'needs_dates' && item.includeInTotal) return undefined
      if (item.cartStatus === 'duration_unavailable') return CHAT_CART_ROW_ACTION.CHOOSE_DATES
      return item.dates ? CHAT_CART_ROW_ACTION.CHOOSE_PACKAGE : CHAT_CART_ROW_ACTION.ADD_DATES
    }
    case 'experience':
      switch (item.cartStatus) {
        case 'needs_tickets': return CHAT_CART_ROW_ACTION.CHOOSE_TICKETS
        case 'tickets_unavailable': return CHAT_CART_ROW_ACTION.CHOOSE_DIFFERENT_TICKETS
        case 'date_unavailable': return CHAT_CART_ROW_ACTION.CHOOSE_DATE
        case 'needs_time':
        case 'time_unavailable': return CHAT_CART_ROW_ACTION.CHOOSE_TIME
        default: return undefined
      }
    case 'flight': {
      // Mirrors CartPanelFlightTile's blocked-flight branch: an expired price can be
      // refreshed in place, while past dates, a withdrawn fare, or a refresh that
      // already failed need a fresh search.
      if (item.isAvailabilityBlocked) {
        const status = item.flightCartItem?.status
        const needsReplacement = status === 'dates_in_past' || status === 'unavailable' || item.hasRefreshFailed
        return needsReplacement ? CHAT_CART_ROW_ACTION.CHANGE_FLIGHT : CHAT_CART_ROW_ACTION.REFRESH_FLIGHT
      }

      switch (item.cartStatus) {
        // A flight still awaiting fare selection has no cart item, so
        // `isAvailabilityBlocked` above can't see it expire - that flag only reads a
        // converted item. The saved search behind it lapses all the same, and once it
        // has there are no fares left to choose: opening the fare picker just yields an
        // empty modal. Offer the refresh instead, matching CartPanelFlightTile.
        case 'needs_flight_selection':
          return isSavedFlightSearchExpired(item) ?
            CHAT_CART_ROW_ACTION.REFRESH_FLIGHT :
            CHAT_CART_ROW_ACTION.CHOOSE_FLIGHT
        case 'needs_flight_offer': return CHAT_CART_ROW_ACTION.CHOOSE_FLIGHT
        default: return undefined
      }
    }
  }
}
